import React, { useState } from 'react';
import {
  Row,
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Label,
  Input,
  FormGroup,
  FormFeedback,
} from 'reactstrap';
import IntlMessages from "../../../../../helpers/IntlMessages";
import { Colxx } from '../../../../../components/common/CustomBootstrap';
import { AxiosAPI } from '../../../../../helpers/AxiosAPI';
import {
  postAPI_Update_Rates,
  gAPI_RatesPlan_List_ByRoomId,
} from '../../../../../constants/APIs';
import currencies from '../../../../../data/currencies';

// Redux
import { injectIntl } from 'react-intl';
import { connect } from 'react-redux';
import Validation from '../../../../../components/Validation';

import { Formik, FieldArray, Form } from 'formik';
import * as Yup from 'yup';

//  Yup validation schema
const RatesSchema = Yup.object().shape({
  rates: Yup.array().of(
    Yup.object().shape({
      roomTypeId: Yup.string().required('Choose the room type!'),
      ratePlanId: Yup.string().required('Choose the rate plan!'),
      startDate: Yup.date().required('Choose the start date!'),
      endDate: Yup.date().required('Choose the end date!'),
      amount: Yup.number()
        .min(0, 'Amount must be >= 0')
        .required('Enter amount'),
      includeTax: Yup.boolean().required('Select tax option'),
      numberOfGuests: Yup.number()
        .min(1, 'At least 1 guest')
        .required('Enter number of guests'),
      currency: Yup.string().required('Select a currency'),
    })
  ),
});

function RatesEditModal({ isVisible, toggleModal, propertyId, roomlist }) {
  const [open, setOpen] = useState(false);
  const [msg, setMsg] = useState([]);
  const [ratePlanlist, setRatePlanlist] = useState([]);

  const handleUpdate = (values) => {
    AxiosAPI({
      url: postAPI_Update_Rates,
      method: 'post',
      data: {
        propertyId,
        data: values.rates,
      },
    })
      .then((res) => {
        if (
          res.data.HttpStatusCode === 400 &&
          res.data.Messages &&
          res.data.Messages.length > 0
        ) {
          setMsg(res.data.Messages);
          setOpen(true);
        } else {
          setTimeout(() => {
            window.location.reload();
          }, 500);
        }
      })
      .catch((err) => {
        console.log('err', err);
      });
  };

  // Sorted currencies
  const selectCurrencies = currencies
    .sort((a, b) => a.label.localeCompare(b.label))
    .map((item) => (
      <option value={item.value} key={item.value}>
        {item.label}
      </option>
    ));

  return (
    <div>
      <Modal size="lg" isOpen={isVisible} toggle={toggleModal}>
        <ModalHeader toggle={toggleModal}>Edit Rates</ModalHeader>

        <Formik
          initialValues={{
            rates: [
              {
                roomTypeId: '',
                ratePlanId: '',
                startDate: '',
                endDate: '',
                amount: 0,
                includeTax: true,
                numberOfGuests: 0,
                currency: 'NZD',
              },
            ],
          }}
          validationSchema={RatesSchema}
          onSubmit={handleUpdate}
        >
          {({ values, errors, touched, handleChange, handleBlur, setFieldValue }) => (
            <Form>
              <ModalBody>
                <FieldArray
                  name="rates"
                  render={(arrayHelpers) => (
                    <>
                      {values.rates.map((rate, index) => (
                        <Row key={index}>
                          {/* Room Type */}
                          <Colxx sm={3}>
                            <FormGroup>
                              <Label>
                                <IntlMessages id="dashboards.room-type" />
                              </Label>
                              <Input className='input_sty'
                                type="select"
                                name={`rates.${index}.roomTypeId`}
                                value={rate.roomTypeId}
                                onChange={(e) => {
                                  handleChange(e);
                                  // ✅ Load rate plans dynamically
                                  AxiosAPI({
                                    method: 'get',
                                    url: `${gAPI_RatesPlan_List_ByRoomId}?roomId=${e.target.value}&status=1`,
                                  })
                                    .then((res) => {
                                      const list = res.data.ratePlans.map((elem) => ({
                                        ratePlanId: elem.id,
                                        ratePlanName: elem.name,
                                      }));
                                      setRatePlanlist(list);
                                    })
                                    .catch((err) => console.log(err));
                                }}
                                onBlur={handleBlur}
                                invalid={
                                  !!(
                                    errors.rates?.[index]?.roomTypeId &&
                                    touched.rates?.[index]?.roomTypeId
                                  )
                                }
                              >
                                <option value="" disabled hidden>
                                  Room Type
                                </option>
                                {roomlist.map((elem) => (
                                  <option value={elem.roomTypeId} key={elem.roomTypeId}>
                                    {elem.roomName}
                                  </option>
                                ))}
                              </Input>
                              {errors.rates?.[index]?.roomTypeId &&
                                touched.rates?.[index]?.roomTypeId && (
                                  <FormFeedback>
                                    {errors.rates[index].roomTypeId}
                                  </FormFeedback>
                                )}
                            </FormGroup>
                          </Colxx>

                          {/* Rate Plan */}
                          <Colxx sm={3}>
                            <FormGroup>
                              <Label>Rate Plan</Label>
                              <Input className='input_sty'
                                type="select"
                                name={`rates.${index}.ratePlanId`}
                                value={rate.ratePlanId}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                invalid={
                                  !!(
                                    errors.rates?.[index]?.ratePlanId &&
                                    touched.rates?.[index]?.ratePlanId
                                  )
                                }
                              >
                                <option value="" disabled hidden>
                                  Rate Plan
                                </option>
                                {ratePlanlist.map((elem) => (
                                  <option value={elem.ratePlanId} key={elem.ratePlanId}>
                                    {elem.ratePlanName}
                                  </option>
                                ))}
                              </Input>
                              {errors.rates?.[index]?.ratePlanId &&
                                touched.rates?.[index]?.ratePlanId && (
                                  <FormFeedback>
                                    {errors.rates[index].ratePlanId}
                                  </FormFeedback>
                                )}
                            </FormGroup>
                          </Colxx>

                          {/* Start Date */}
                          <Colxx sm={3}>
                            <FormGroup>
                              <Label>
                                <IntlMessages id="dashboards.start-date" />
                              </Label>
                              <Input className='input_sty'
                                type="date"
                                name={`rates.${index}.startDate`}
                                value={rate.startDate}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                invalid={
                                  !!(
                                    errors.rates?.[index]?.startDate &&
                                    touched.rates?.[index]?.startDate
                                  )
                                }
                              />
                              {errors.rates?.[index]?.startDate &&
                                touched.rates?.[index]?.startDate && (
                                  <FormFeedback>
                                    {errors.rates[index].startDate}
                                  </FormFeedback>
                                )}
                            </FormGroup>
                          </Colxx>

                          {/* End Date */}
                          <Colxx sm={3}>
                            <FormGroup>
                              <Label>
                                <IntlMessages id="dashboards.end-date" />
                              </Label>
                              <Input className='input_sty'
                                type="date"
                                name={`rates.${index}.endDate`}
                                value={rate.endDate}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                invalid={
                                  !!(
                                    errors.rates?.[index]?.endDate &&
                                    touched.rates?.[index]?.endDate
                                  )
                                }
                              />
                              {errors.rates?.[index]?.endDate &&
                                touched.rates?.[index]?.endDate && (
                                  <FormFeedback>
                                    {errors.rates[index].endDate}
                                  </FormFeedback>
                                )}
                            </FormGroup>
                          </Colxx>

                          {/* Amount */}
                          <Colxx sm={2}>
                            <FormGroup>
                              <Label>Amount</Label>
                              <Input className='input_sty'
                                type="number"
                                name={`rates.${index}.amount`}
                                value={rate.amount}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                min={0}
                                invalid={
                                  !!(
                                    errors.rates?.[index]?.amount &&
                                    touched.rates?.[index]?.amount
                                  )
                                }
                              />
                              {errors.rates?.[index]?.amount &&
                                touched.rates?.[index]?.amount && (
                                  <FormFeedback>
                                    {errors.rates[index].amount}
                                  </FormFeedback>
                                )}
                            </FormGroup>
                          </Colxx>

                          {/* Include Tax */}
                          <Colxx sm={2}>
                            <FormGroup>
                              <Label>Include Tax?</Label>
                              <Input className='input_sty'
                                type="select"
                                name={`rates.${index}.includeTax`}
                                value={rate.includeTax}
                                onChange={(e) =>
                                  setFieldValue(
                                    `rates.${index}.includeTax`,
                                    e.target.value === 'true'
                                  )
                                }
                                onBlur={handleBlur}
                                invalid={
                                  !!(
                                    errors.rates?.[index]?.includeTax &&
                                    touched.rates?.[index]?.includeTax
                                  )
                                }
                              >
                                <option value={false}>No</option>
                                <option value={true}>Yes</option>
                              </Input>
                              {errors.rates?.[index]?.includeTax &&
                                touched.rates?.[index]?.includeTax && (
                                  <FormFeedback>
                                    {errors.rates[index].includeTax}
                                  </FormFeedback>
                                )}
                            </FormGroup>
                          </Colxx>

                          {/* Guests */}
                          <Colxx sm={3}>
                            <FormGroup>
                              <Label>Number of Guests</Label>
                              <Input className='input_sty'
                                type="number"
                                name={`rates.${index}.numberOfGuests`}
                                value={rate.numberOfGuests}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                invalid={
                                  !!(
                                    errors.rates?.[index]?.numberOfGuests &&
                                    touched.rates?.[index]?.numberOfGuests
                                  )
                                }
                              />
                              {errors.rates?.[index]?.numberOfGuests &&
                                touched.rates?.[index]?.numberOfGuests && (
                                  <FormFeedback>
                                    {errors.rates[index].numberOfGuests}
                                  </FormFeedback>
                                )}
                            </FormGroup>
                          </Colxx>

                          {/* Currency */}
                          <Colxx sm={3}>
                            <FormGroup>
                              <Label>Currency</Label>
                              <Input className='input_sty'
                                type="select"
                                name={`rates.${index}.currency`}
                                value={rate.currency}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                invalid={
                                  !!(
                                    errors.rates?.[index]?.currency &&
                                    touched.rates?.[index]?.currency
                                  )
                                }
                              >
                                {selectCurrencies}
                              </Input>
                              {errors.rates?.[index]?.currency &&
                                touched.rates?.[index]?.currency && (
                                  <FormFeedback>
                                    {errors.rates[index].currency}
                                  </FormFeedback>
                                )}
                            </FormGroup>
                          </Colxx>

                          {/* Add/Remove Buttons */}
                          <Colxx sm={12} className="mb-3">
                            <div className="d-flex align-items-center">
                              <Button
                                className="mr-2"
                                onClick={() =>
                                  arrayHelpers.push({
                                    roomTypeId: '',
                                    ratePlanId: '',
                                    startDate: '',
                                    endDate: '',
                                    amount: 0,
                                    includeTax: true,
                                    numberOfGuests: 0,
                                    currency: 'NZD',
                                  })
                                }
                                color="primary"
                                size="xs"
                                outline
                                type="button"
                              >
                                + Add More
                              </Button>
                              {values.rates.length > 1 && (
                                <Button
                                  onClick={() => arrayHelpers.remove(index)}
                                  color="secondary"
                                  size="xs"
                                  outline
                                  type="button"
                                >
                                  -
                                </Button>
                              )}
                            </div>
                          </Colxx>
                        </Row>
                      ))}
                    </>
                  )}
                />
              </ModalBody>

              <ModalFooter>
                <Button color="primary" type="submit">
                  Update
                </Button>
                <Button color="secondary" onClick={toggleModal} type="button">
                  Cancel
                </Button>
              </ModalFooter>
            </Form>
          )}
        </Formik>
      </Modal>

      {/*  Validation popup */}
      <Validation
        open={open}
        messages={msg}
        toggle={() => {
          setOpen(!open);
        }}
      />
    </div>
  );
}

const mapStateToProps = ({ settings }) => {
  const { propertyId } = settings;
  return {
    propertyId,
  };
};

export default injectIntl(connect(mapStateToProps, {})(RatesEditModal));
